import orderBy from 'lodash-es/orderBy';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { UserRoles } from 'uplisting-frontend/models/user';
import {
  TEAMMATE_SHAPES,
  type TeammateShapeKey,
} from 'uplisting-frontend/utils/teammate-shapes';

export type PersonRoleKey = TeammateShapeKey | UserRoles.accountOwner;
export type PersonKind = 'owner' | 'teammate' | 'invitation';
export type PersonSortColumn = 'name' | 'role';
export type SortDirection = 'asc' | 'desc';

export interface PersonRow {
  id: string;
  kind: PersonKind;
  name: string;
  initials: string;
  email: string;
  roleKey: PersonRoleKey;
  roleLabel: string;
  propertyNames: string[];
  unitNames: string[];
  hasAllProperties: boolean;
  isPending: boolean;
  invitedAt?: Date;
  resentAt?: Date;
  isDisabled: boolean;
  teammate?: ModelRegistry['teammate'];
  invitation?: ModelRegistry['invitation'];
}

export interface RoleOption {
  key: PersonRoleKey;
  label: string;
}

export const ACCOUNT_OWNER_ROLE_KEY = UserRoles.accountOwner;

export const ROLE_ORDER: PersonRoleKey[] = [
  UserRoles.accountOwner,
  ...TEAMMATE_SHAPES.map((shape) => shape.key),
];

const SORT_ACCESSORS: Record<PersonSortColumn, (row: PersonRow) => string> = {
  name: (row) => row.name.toLowerCase(),
  role: (row) => row.roleLabel.toLowerCase(),
};

const roleRank = (row: PersonRow): number => ROLE_ORDER.indexOf(row.roleKey);

export function filterPeople(
  rows: PersonRow[],
  search: string,
  roleKeys: PersonRoleKey[],
): PersonRow[] {
  const byRole = roleKeys.length
    ? rows.filter((row) => roleKeys.includes(row.roleKey))
    : rows;

  const needle = search.trim().toLowerCase();

  if (!needle) {
    return byRole;
  }

  return byRole.filter(
    (row) =>
      row.name.toLowerCase().includes(needle) ||
      row.email.toLowerCase().includes(needle),
  );
}

export function sortPeople(
  rows: PersonRow[],
  column: PersonSortColumn | undefined,
  direction: SortDirection,
): PersonRow[] {
  if (!column) {
    return orderBy(rows, [roleRank, SORT_ACCESSORS.name], ['asc', 'asc']);
  }

  return orderBy(
    rows,
    [SORT_ACCESSORS[column], SORT_ACCESSORS.name],
    [direction, 'asc'],
  );
}
